import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of, throwError, forkJoin} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {plainToClass} from 'class-transformer';
import {O7Device} from '../classes/o7-device';
import * as _ from 'lodash';
import {AppInfo, O7DeviceInfo} from '../classes/o7-device-info';
import {O7dDeviceDetailDto} from '../classes/o7d-device-detail-dto';
import {O7appsUserAppDetailDto, O7dUserDetailDto} from '../classes/o7d-user-detail-dto';

@Injectable({
  providedIn: 'root'
})
export class O7DevicesService {

  private static readonly BASE_URL: string = '/o7devices';
  private cachedO7Devices: O7Device[] = null;

  constructor(private http: HttpClient) {}

  private mapV2ToV1s(v2Device: O7dDeviceDetailDto): O7Device[] {
    return _.map(v2Device.userIds, (userId: string) => plainToClass(O7Device, {
      changeLog: [],
      dateCreated: new Date(),
      displayName: `${_.startCase(v2Device.manufacturerId)} ${v2Device.marketingName} (${userId})`,
      model: v2Device.model,
      name: v2Device.marketingName,
      os: v2Device.osVersion,
      owner: v2Device.owner,
      platform: v2Device.osId === 'android' ?
        'Android' :
        (v2Device.typeId === 'phone' ? 'iPhone' : 'iPad'),
      releaseYear: 0,
      requestLog: [],
      serialNumber: v2Device.serial,
      udid: '',
      udidHashHashCode: 0,
      uid: userId
    }));
  }

  private mapAppDetailToInfo(apps: O7appsUserAppDetailDto[], installed: boolean): AppInfo[] {
    return apps
      .filter((app: O7appsUserAppDetailDto) => app.installed === installed)
      .map((app: O7appsUserAppDetailDto) => ({
        appId: app.appId,
        uniqueAppId: app.uniqueId,
        appName: app.appTitleInSheet,
        appClass: undefined,
        appClassId: app.appClassId,
        appUserId: undefined,
        appStore: app.appStore
      }));
  }

  getO7Devices(): Observable<O7Device[]> {
    if (this.cachedO7Devices != null) {
      return of(this.cachedO7Devices);
    }

    const v2Devices$: Observable<O7dDeviceDetailDto[]> = this.http.get<O7dDeviceDetailDto[]>(O7DevicesService.BASE_URL + '/devices/detail');

    return v2Devices$
      .pipe(
        map(result => {
          const v2Devices: O7dDeviceDetailDto[] = plainToClass(O7dDeviceDetailDto, result);
          const v2DevicesAsV1: O7Device[] = _.flatMap(v2Devices, (v2Device: O7dDeviceDetailDto) => this.mapV2ToV1s(v2Device));
          return v2DevicesAsV1;
        }),
        tap((o7devices: O7Device[]) => {
          this.cachedO7Devices = o7devices;
        })
      );
  }

  getCachedO7Devices(): O7Device[] {
    if (this.cachedO7Devices == null) {
      throw new Error('O7 devices not fetched yet. Use o7 devices resolver!');
    }
    return this.cachedO7Devices;
  }

  findO7Device(uid: string): O7Device | undefined {
    return _.find(this.getCachedO7Devices(), {uid: uid});
  }

  getO7DeviceByUid(uid: string): Observable<O7Device> {
    return this.http.get<O7dDeviceDetailDto>(O7DevicesService.BASE_URL + '/devices/users/' + uid)
      .pipe(
        map((result: O7dDeviceDetailDto) => {
          const v2DevicesAsV1: O7Device[] = this.mapV2ToV1s(result);
          return v2DevicesAsV1.find((device: O7Device) => device.uid === uid);
        }),
        catchError(err => {
          console.error('Error', err);
          return throwError(err);
        })
      );
  }

  getO7DeviceInfo(uid: string): Observable<O7DeviceInfo> {
    return forkJoin([
      this.getO7DeviceByUid(uid),
      this.http.get<O7dUserDetailDto>(O7DevicesService.BASE_URL + `/users/${uid}/detail`)
    ]).pipe(
      map(([o7Device, user]) => {
        const appDetails: O7appsUserAppDetailDto[] = user.o7appsUserDetail?.apps ?? [];

        return plainToClass(O7DeviceInfo, {
          o7Device,
          user,
          installedApps: this.mapAppDetailToInfo(appDetails, true),
          uninstalledApps: this.mapAppDetailToInfo(appDetails, false)
        });
      })
    );
  }
}
